<template>
    <div class="private-sale-show">
        <div class="page-heading">
            <div>
                <div class="title-row">
                    <h1>{{ sale.name }}</h1>
                    <span class="status" :class="`status-${statusLabel}`">{{ statusLabel }}</span>
                </div>
                <p class="subtitle">{{ sale.slug }}</p>
            </div>
            <a class="btn-outline" :href="payload.routes.index">Torna all’elenco</a>
        </div>

        <div class="show-panel">
            <dl class="details">
                <div>
                    <dt>Tipo</dt>
                    <dd><sale-type-badge :label="sale.type" /></dd>
                </div>
                <div>
                    <dt>Inizio</dt>
                    <dd>{{ datePart(sale.start) }} {{ timePart(sale.start) }}</dd>
                </div>
                <div>
                    <dt>Fine</dt>
                    <dd>{{ datePart(sale.end) }} {{ timePart(sale.end) }}</dd>
                </div>
                <div class="detail-wide">
                    <dt>Descrizione</dt>
                    <dd>{{ sale.description || '—' }}</dd>
                </div>
            </dl>

            <h2>Prodotti</h2>
            <p v-if="!products.length" class="subtitle">Nessun prodotto associato.</p>
            <table v-else class="products-table" aria-label="Prodotti della vendita">
                <thead>
                    <tr>
                        <th scope="col">Prodotto</th>
                        <th scope="col">EAN</th>
                        <th scope="col">Quantità</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="line in products" :key="line.ean">
                        <td>{{ line.text || line.ean }}</td>
                        <td class="ean">{{ line.ean }}</td>
                        <td>{{ quantityLabel(line.available_quantity) }}</td>
                    </tr>
                </tbody>
            </table>

            <div v-if="sale.deleted" class="restore-row">
                <button type="button" class="btn-primary" :disabled="saving" @click="restoreSale">Ripristina vendita</button>
            </div>
        </div>
    </div>
</template>

<script>
import SaleTypeBadge from '../sale-type-badge/SaleTypeBadge.vue';

export default {
    name: 'PrivateSaleShow',
    components: { SaleTypeBadge },
    props: {
        payload: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            sale: { ...(this.payload.sale || {}) },
            saving: false,
        };
    },
    computed: {
        products() {
            return this.sale.products || [];
        },
        statusLabel() {
            return this.sale.deleted ? 'eliminata' : this.sale.status;
        },
    },
    methods: {
        datePart(value) {
            const [day = ''] = String(value || '').split('T');
            const [year, month, d] = day.split('-');
            if (!year || !month || !d) {
                return '—';
            }
            return `${d}/${month}/${year}`;
        },
        timePart(value) {
            return String(value || '').split('T')[1] || '';
        },
        quantityLabel(quantity) {
            return quantity === null || quantity === undefined || quantity === '' ? 'nessun tetto' : quantity;
        },
        async restoreSale() {
            if (this.saving) {
                return;
            }
            this.saving = true;
            try {
                const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const response = await fetch(this.payload.routes.restore, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': token,
                    },
                    credentials: 'same-origin',
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || 'Ripristino non riuscito.');
                }
                this.sale = { ...this.sale, ...(data.sale || {}), deleted: false };
                if (window.Swal) {
                    window.Swal.fire({
                        text: data.message || 'Vendita ripristinata.',
                        icon: 'success',
                        buttonsStyling: false,
                        timer: 2200,
                        showConfirmButton: false,
                    });
                }
            } catch (error) {
                if (window.Swal) {
                    window.Swal.fire({
                        text: error.message,
                        icon: 'error',
                        buttonsStyling: false,
                        confirmButtonText: 'Ok',
                        customClass: { confirmButton: 'btn btn-danger' },
                    });
                }
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.private-sale-show {
    max-width: 1400px;
    margin-left: auto;
    margin-right: auto;
    color: #1f1f1f;
    font-family: inherit;
    font-size: 14px;
}
.page-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    margin: 0 0 28px;
}
.title-row {
    display: flex;
    align-items: center;
    gap: 12px;
}
h1, h2 { margin: 0; font-weight: 650; line-height: 1.3; }
h1 { font-size: 26px; }
h2 { margin: 22px 0 12px; font-size: 18px; }
.subtitle { margin: 8px 0 0; color: #6b7280; font-size: 14px; }
.btn-outline, .btn-primary {
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 14px;
    border-radius: 6px;
    font: inherit;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
}
.btn-outline { background: #fff; border: 1px solid #e5e7eb; color: #1f1f1f; }
.btn-primary { background: #f39c12; border: 0; color: #fff; }
.show-panel {
    background: #fff;
    border: 1px solid #f5c96a;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(243, 156, 18, 0.12);
}
.details {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 24px;
    margin: 0;
}
.detail-wide { grid-column: 1 / -1; }
dt { color: #1f1f1f; font-size: 16px; font-weight: 700; }
dd { margin: 6px 0 0; font-size: 14px; font-weight: 500; }
.products-table { width: 100%; border-collapse: collapse; }
.products-table th, .products-table td {
    padding: 12px 14px;
    border-bottom: 1px solid #e5e7eb;
    border-left: 1px solid #e5e7eb;
    text-align: left;
}
.products-table th:first-child,
.products-table td:first-child { border-left: 0; }
.products-table th {
    color: #1f1f1f;
    font-size: 15px;
    font-weight: 700;
    background: #fff6e0;
    border-bottom: 2px solid #f3c20f;
    border-left-color: #f5c96a;
}
.ean { color: #6b7280; }
.status {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
}
.status-aperta { color: #fff; background: #f39c12; }
.status-futura { color: #92400e; background: #fef3c7; border: 1px solid #fcd34d; }
.status-chiusa { color: #fff; background: #6b7280; }
.status-eliminata { color: #fff; background: #dd4b39; }
.restore-row { margin-top: 18px; }
</style>
